import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/utils';

// ============================================================================
// Variants
// ============================================================================

const featuredIconVariants = cva(
  'inline-flex items-center justify-center transition-[background-color,color] duration-(--duration-base) ease-standard motion-reduce:transition-none',
  {
    variants: {
      size: {
        xs: 'size-7 p-1.5 [&>svg]:size-4',
        sm: 'size-8 p-2 [&>svg]:size-4',
        md: 'size-9 p-2.5 [&>svg]:size-4',
        lg: 'size-10 p-2.5 [&>svg]:size-5',
      },
      shape: {
        rounded: 'rounded-xl',
        round: 'rounded-full',
      },
      intent: {
        default: 'bg-primary/10 text-primary dark:bg-primary/20',
        success: 'bg-success/10 text-success dark:bg-success/20',
        warning: 'bg-warning/10 text-warning dark:bg-warning/20',
        error: 'bg-destructive/10 text-destructive dark:bg-destructive/20',
        info: 'bg-info/10 text-info dark:bg-info/20',
      },
    },
    defaultVariants: {
      size: 'lg',
      shape: 'rounded',
      intent: 'default',
    },
  },
);

// ============================================================================
// Types
// ============================================================================

export interface FeaturedIconProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof featuredIconVariants> {
  /** Icon element (preferred) */
  icon?: React.ReactNode;
  /** Fallback for icon */
  children?: React.ReactNode;
  /** Accessible label — when provided, sets role="img" instead of aria-hidden */
  'aria-label'?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * FeaturedIcon displays an icon within a styled container with customizable size, shape, and color.
 * Useful for highlighting actions, statuses, or categories in cards, lists, and navigation.
 *
 * @llms Styled icon container with size (xs-lg), shape (rounded/round), and color variants
 * @example
 * ```tsx
 * <FeaturedIcon icon={<Settings />} size="md" shape="rounded" />
 * ```
 */
export const FeaturedIcon = React.forwardRef<HTMLDivElement, FeaturedIconProps>(
  (
    {
      className,
      size,
      shape,
      intent,
      icon,
      children,
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    const iconContent = icon ?? children;
    const isDecorative = !ariaLabel;

    if (process.env.NODE_ENV === 'development' && !iconContent) {
      console.warn('FeaturedIcon: Either icon or children prop is required');
    }

    return (
      // biome-ignore lint/a11y/useAriaPropsSupportedByRole: role and aria-label are conditionally paired at runtime
      <div
        ref={ref}
        data-slot="featured-icon"
        role={isDecorative ? undefined : 'img'}
        aria-hidden={isDecorative ? true : undefined}
        aria-label={isDecorative ? undefined : ariaLabel}
        className={cn(featuredIconVariants({ size, shape, intent }), className)}
        {...props}
      >
        {iconContent}
      </div>
    );
  },
);

FeaturedIcon.displayName = 'FeaturedIcon';

export { featuredIconVariants };
